import React, { useRef } from 'react';
import { Typography, makeStyles, Theme, Paper, Grid, CardContent, Card } from '@material-ui/core';
import Popover from 'components/@common/Popover';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import Skeleton from 'components/@common/Skeleton';

type Props = {
  enrichmentActivities: any;
  teacherCreatedActivities: any;
  loading?: boolean;
};

const EnrichmentActivityCard: React.FC<Props> = ({
  loading,
  enrichmentActivities,
  teacherCreatedActivities
}) => {
  const containerRef = useRef<HTMLElement>();
  const classes = useStyles();
  const { t } = useTranslation('Reports');
  return (
    <Paper ref={containerRef} className={classes.cardContainer}>
      <Popover description={t('enrichmentActivitiesCompleted', { ns: 'ReportsHelpCards' })}>
        <Typography component="h3" variant="body2">
          {t('Enrichment Activities Completed')}
        </Typography>
      </Popover>
      {!loading && enrichmentActivities ? (
        <div className={classes.primaryStatContainer}>
          <EnrichmentStatCard
            disabled={!teacherCreatedActivities}
            title={t('Teacher Custom Creation')}
            completedCount={teacherCreatedActivities?.totalCompletedCount}
            averageWpm={teacherCreatedActivities?.averageWpm}
            averageAccuracy={teacherCreatedActivities?.averageAccuracy}
          />
          <EnrichmentStatCard
            disabled={!enrichmentActivities.myChoiceKeyboarding}
            title={t('My Choice Keyboarding')}
            completedCount={enrichmentActivities.myChoiceKeyboarding?.totalCompletedCount}
          />
          <EnrichmentStatCard
            disabled={!enrichmentActivities.hearItTypeIt}
            title={t('Hear It, Type It')}
            completedCount={enrichmentActivities.hearItTypeIt?.totalCompletedCount}
          />
        </div>
      ) : (
        <Skeleton height={containerRef.current?.clientHeight!} />
      )}
    </Paper>
  );
};

const EnrichmentStatCard: React.FC<{
  title: string;
  disabled: boolean;
  completedCount?: number;
  averageWpm?: number;
  averageAccuracy?: number;
}> = ({ title, disabled, completedCount, averageWpm, averageAccuracy }): React.ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('Reports');

  return (
    <Card
      variant="outlined"
      className={cx(classes.card, {
        [classes.disabledCardContent]: disabled
      })}
    >
      <CardContent className={classes.cardContent}>
        <Typography variant="body2" gutterBottom>
          {title}
        </Typography>
        {!disabled ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={4}>
              <Typography variant="h3" component="span">
                {completedCount}
              </Typography>
              <Typography component="span" className={classes.descriptorLabel}>
                {t('completed')}
              </Typography>
            </Grid>
            {!isNil(averageWpm) && (
              <Grid item xs={12} sm={12} md={4}>
                <Typography variant="h3" component="span">
                  {averageWpm}
                </Typography>
                <Typography component="span" className={classes.descriptorLabel}>
                  {t('words per minute')}
                </Typography>
              </Grid>
            )}
            {!isNil(averageAccuracy) && (
              <Grid item xs={12} sm={12} md={4} className={classes.flexWrap}>
                <Typography variant="h3" component="span">
                  {averageAccuracy}%
                </Typography>
                <Typography component="span" className={classes.descriptorLabel}>
                  {t('accuracy')}
                </Typography>
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography>{t('Disabled')}</Typography>
        )}
      </CardContent>
    </Card>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  disabledCardContent: {
    color: theme.palette.grey[500],
    backgroundColor: theme.palette.grey[100]
  },
  cardContent: {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(1)
    }
  },
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    height: '100%'
  },
  primaryStatContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  descriptorLabel: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.grey[600]
  },
  flexWrap: {
    display: 'flex',
    flexWrap: 'wrap'
  }
}));

export default EnrichmentActivityCard;
